import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { FiClock, FiInbox, FiLoader } from 'react-icons/fi';
import type { AgentVersionResponse } from '@troia-v3/types';

import {
  useAgentVersions,
  useRestoreVersion,
} from '../../hooks/useAgentVersions';
import { PromptDiffModal } from '../PromptDiffModal';

import { VersionRow } from './VersionRow';

interface VersionsTabProps {
  agentId: string;
  /** Prompt atual do form (pode ter mudanças não salvas) */
  currentPrompt: string;
}

/**
 * Tab "Versões" dentro do AgentFormPage. Lista o histórico de versões
 * com badges de score (Sprint UI-4).
 *
 * Cada linha mostra:
 * - Versão + tipo (major/minor/patch/initial) + data
 * - Score agregado dos snapshots vinculados àquela versão
 * - Quantos treinos rodaram naquela versão
 * - Botões "Comparar" (PromptDiffModal) e "Restaurar"
 *
 * "Restaurar" cria uma NOVA versão com o systemPrompt da versão antiga,
 * preservando o histórico (não modifica/deleta nada).
 */
export const VersionsTab: React.FC<VersionsTabProps> = ({
  agentId,
  currentPrompt,
}) => {
  const { data, isLoading, error } = useAgentVersions(agentId, {
    page: 1,
    limit: 50,
  });
  const restoreMutation = useRestoreVersion(agentId);

  const [comparingVersion, setComparingVersion] =
    useState<AgentVersionResponse | null>(null);
  const [confirmingRestore, setConfirmingRestore] =
    useState<AgentVersionResponse | null>(null);

  const handleConfirmRestore = async (): Promise<void> => {
    if (!confirmingRestore) return;
    try {
      await restoreMutation.mutateAsync({ versionId: confirmingRestore.id });
      setConfirmingRestore(null);
    } catch (err) {
      void err;
    }
  };

  // A versão "ativa" é a mais recente (data?.items[0])
  const activeVersionId = data?.items?.[0]?.id;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2
          className="text-lg font-semibold"
          style={{ color: 'var(--theme-card-text)' }}
        >
          Histórico de versões
        </h2>
        <p
          className="text-sm mt-1"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          Cada vez que você salva uma alteração no prompt, uma nova versão é
          criada. Aqui você vê os scores de qualidade de cada versão e pode
          restaurar uma anterior se precisar.
        </p>
      </div>

      {/* Loading */}
      {isLoading && (
        <div
          className="rounded-lg border p-8 text-center"
          style={{
            backgroundColor: 'var(--theme-card-background)',
            borderColor: 'var(--theme-card-border)',
            color: 'var(--theme-muted-text)',
          }}
        >
          <FiLoader className="h-6 w-6 mx-auto mb-2 animate-spin" />
          <div className="text-sm">Carregando versões...</div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div
          className="rounded-lg border p-4 text-sm"
          style={{
            backgroundColor: '#fef2f2',
            borderColor: '#fecaca',
            color: '#991b1b',
          }}
        >
          Erro ao carregar versões: {error.message}
        </div>
      )}

      {/* Empty */}
      {!isLoading && !error && data && data.items.length === 0 && (
        <div
          className="rounded-lg border p-8 text-center"
          style={{
            backgroundColor: 'var(--theme-card-background)',
            borderColor: 'var(--theme-card-border)',
          }}
        >
          <FiInbox
            className="h-10 w-10 mx-auto mb-3"
            style={{ color: 'var(--theme-muted-text)' }}
          />
          <div
            className="text-base font-semibold mb-1"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Nenhuma versão registrada ainda
          </div>
          <p
            className="text-sm"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            Salve uma alteração no prompt do agente para que a primeira
            versão seja registrada.
          </p>
        </div>
      )}

      {/* Lista */}
      {!isLoading && data && data.items.length > 0 && (
        <>
          <div
            className="text-xs flex items-center gap-2"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            <FiClock className="h-3 w-3" />
            {data.total} {data.total === 1 ? 'versão' : 'versões'} no histórico
          </div>

          <div className="space-y-3">
            {data.items.map((version, idx) => {
              // Lista vem ordenada DESC por data. A "versão anterior" é o
              // próximo item (idx+1) — a última versão da lista não tem
              // anterior.
              const previousVersion = data.items[idx + 1];
              return (
                <VersionRow
                  key={version.id}
                  agentId={agentId}
                  version={version}
                  isActive={version.id === activeVersionId}
                  previousVersionId={previousVersion?.id}
                  previousVersionLabel={
                    previousVersion ? `v${previousVersion.version}` : undefined
                  }
                  onCompare={(v) => setComparingVersion(v)}
                  onRestore={(v) => setConfirmingRestore(v)}
                  restoring={
                    restoreMutation.isPending &&
                    restoreMutation.variables?.versionId === version.id
                  }
                />
              );
            })}
          </div>
        </>
      )}

      {/* Diff modal de comparação */}
      {comparingVersion && (
        <PromptDiffModal
          isOpen={true}
          onClose={() => setComparingVersion(null)}
          onConfirm={() => setComparingVersion(null)}
          originalPrompt={comparingVersion.systemPrompt}
          newPrompt={currentPrompt}
          mode="save"
        />
      )}

      {/* Modal de confirmação de restore */}
      {confirmingRestore && (
        <RestoreConfirmModal
          version={confirmingRestore}
          restoring={restoreMutation.isPending}
          error={restoreMutation.error?.message ?? null}
          onCancel={() => setConfirmingRestore(null)}
          onConfirm={handleConfirmRestore}
        />
      )}
    </div>
  );
};

// ─── Modal de confirmação ───────────────────────────────────────────────

interface RestoreConfirmModalProps {
  version: AgentVersionResponse;
  restoring: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}

const RestoreConfirmModal: React.FC<RestoreConfirmModalProps> = ({
  version,
  restoring,
  error,
  onCancel,
  onConfirm,
}) => {
  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
    >
      <div
        className="relative w-full max-w-md rounded-xl shadow-2xl overflow-hidden"
        style={
          {
            backgroundColor: '#ffffff',
            '--theme-text': '#1e293b',
            '--theme-muted-text': '#6b7280',
            '--theme-card-text': '#334155',
          } as React.CSSProperties
        }
      >
        <div className="p-5">
          <h3 className="text-lg font-semibold text-slate-800 mb-2">
            Restaurar versão {version.version}?
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            Isso vai criar uma <strong>nova versão</strong> do agente com o
            mesmo conteúdo desta. O histórico atual continua preservado — você
            pode voltar atrás depois se quiser.
          </p>

          {error && (
            <div
              className="text-xs rounded px-3 py-2 mb-3"
              style={{
                backgroundColor: '#fef2f2',
                color: '#991b1b',
                border: '1px solid #fecaca',
              }}
            >
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={restoring}
              className="px-4 py-2 rounded-md text-sm font-medium"
              style={{ color: '#64748b' }}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => void onConfirm()}
              disabled={restoring}
              className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold"
              style={{
                backgroundColor: '#8b5cf6',
                color: '#ffffff',
                cursor: restoring ? 'wait' : 'pointer',
                opacity: restoring ? 0.6 : 1,
              }}
            >
              {restoring && <FiLoader className="h-4 w-4 animate-spin" />}
              Restaurar
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
